import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { FormTitle } from '../customComponents/FormTitle';
import { useLang } from '../context/LangContext';
import CustomTiptapEditor from './Editor/CustomTiptapEditor';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 24px;
`;

const Content = styled.div`
  padding: 16px 0;
`;

// 에디터 컨테이너 (읽기 전용 모드용)
const EditorContainer = styled.div<{ $isExpanded: boolean }>`
  background-color: #f3f6f6;
  border-radius: 12px;
  overflow: hidden;
  
  .editor-container {
    height: auto !important;
    min-height: auto !important;
    border-radius: 12px;
    overflow: hidden;
  }
  
  .tiptap-editor-content {
    height: auto !important;
    min-height: auto !important;
    max-height: ${({ $isExpanded }) => $isExpanded ? 'none' : '150px'};
    overflow: hidden;
    border-radius: 12px;
    
    @media (min-width: 768px) {
      max-height: ${({ $isExpanded }) => $isExpanded ? 'none' : '200px'};
    }
  }
  
  .tiptap-editor-content .ProseMirror {
    padding: 16px;
    font-size: 14px;
    color: #333;
    line-height: 1.6;
    text-align: left;
    background: transparent;
    border: none;
    outline: none;
    border-radius: 12px;
  }
  
  /* .tiptap-editor-content .ProseMirror:focus {
    outline: none;
  } */
  
  
  .tiptap-editor-content .ProseMirror img {
    border-radius: 8px;
    max-width: 100%;
    height: auto;
  }
`;

// 더보기/접기 버튼
const ToggleButton = styled.button`
  background: none;
  border: none;
  color: #297873;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 0 0 0;
  text-align: center;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  line-height: 14px;
  height: 30px;
  margin-bottom: 8px;
  
  &:hover {
    text-decoration: underline;
  }
`;

// 화살표 아이콘 (CSS로 chevron 생성)
const ArrowIcon = styled.span<{ $isExpanded: boolean }>`
  width: 8px;
  height: 8px;
  border-right: 1px solid #297873;
  border-bottom: 1px solid #297873;
  transition: transform 0.2s ease;
  transform: ${({ $isExpanded }) => $isExpanded 
    ? 'rotate(-135deg) translateY(-3px)' 
    : 'rotate(45deg) translateY(-3px)'};
  flex-shrink: 0;
  margin-left: 4px;
`;

interface LotDescriptionProps {
  title?: string;
  description?: string;
  isVisible?: boolean; // 컴포넌트가 화면에 보이는지 여부
}

// 다국어 텍스트
const lotDescriptionTexts = {
  ko: {
    title: 'LOT 설명',
    showMore: '더보기',
    showLess: '접기'
  },
  en: {
    title: 'LOT Description',
    showMore: 'Show More',
    showLess: 'Show Less'
  }
};

const LotDescription: React.FC<LotDescriptionProps> = ({ 
  title,
  description,
  isVisible = true
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldShowToggle, setShouldShowToggle] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const { lang } = useLang();
  const texts = lotDescriptionTexts[lang];
  
  // props로 받은 값이 없으면 빈 문자열 사용 (더미 데이터 제거)
  const sectionTitle = title || texts.title;
  const rawDescription = description || '';

  useEffect(() => {
    // 에디터 컨테이너의 높이를 체크하여 더보기 버튼 표시 여부 결정
    const checkContentOverflow = () => {
      if (editorRef.current && isVisible) {
        const editorContent = editorRef.current.querySelector('.tiptap-editor-content');
        if (editorContent) {
          const isWeb = window.innerWidth >= 768;
          const maxHeight = isWeb ? 135 : 90; // px 단위
          
          // 콘텐츠를 잠시 펼쳐서 실제 높이 측정
          (editorContent as HTMLElement).style.maxHeight = 'none';
          const actualHeight = editorContent.scrollHeight;
          
          // 다시 원래 상태로 복원
          if (!isExpanded) {
            (editorContent as HTMLElement).style.maxHeight = `${maxHeight}px`;
          }
          
          setShouldShowToggle(actualHeight > maxHeight);
        }
      }
    };

    // isVisible이 true가 되면 약간의 지연 후 체크 (DOM 렌더링 완료 대기)
    if (isVisible) {
      const timeoutId = setTimeout(checkContentOverflow, 300);
      return () => clearTimeout(timeoutId);
    } else {
      checkContentOverflow();
    }

    // 윈도우 리사이즈 이벤트 리스너 추가
    const handleResize = () => {
      if (isVisible) {
        checkContentOverflow();
      }
    };

    window.addEventListener('resize', handleResize);

    // cleanup 함수로 이벤트 리스너 제거
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [rawDescription, isExpanded, isVisible]);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  // description이 비어있으면 컴포넌트를 렌더링하지 않음
  if (!rawDescription || rawDescription.trim() === '') {
    return null;
  }

  return (
    <Container>
      <FormTitle>{sectionTitle}</FormTitle>
      <Content>
        <EditorContainer ref={editorRef} $isExpanded={isExpanded}>
          <CustomTiptapEditor
            content={rawDescription}
            readOnly={true}
            showMenuBar={false}
            enableImageUpload={false}
            height="auto"
            hideBorder={true}
          />
                 {shouldShowToggle && (
          <ToggleButton onClick={handleToggle}>
            {isExpanded ? texts.showLess : texts.showMore}
            <ArrowIcon $isExpanded={isExpanded} />
          </ToggleButton>
        )}
        </EditorContainer>
 
      </Content>
    </Container>
  );
};

export default LotDescription;
